import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { moduleApi, notesApi, authApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { 
    Calendar as CalendarIcon, 
    Plus, 
    Archive, 
    Folder,
    FolderOpen,
    Clock, 
    Users, 
    MapPin, 
    ChevronRight,
    AlertCircle,
    CheckCircle2,
    History,
    X,
    Building2,
    User,
    UserCheck,
    Settings,
    ChevronDown,
    Search,
    Check,
    Save,
    ToggleLeft,
    ToggleRight,
    Send,
    Phone,
    Mail,
    Trash2,
    MoreHorizontal,
    FileText,
    Paperclip,
    ExternalLink,
    Briefcase,
    Utensils
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { InfoModal } from '../InfoModal';
import { EventForm } from './EventForm';
import { ExtrasInternalTypesSettings } from './ExtrasInternalTypesSettings';
import { COLLECTE_STATUS_LABELS } from './collecteStatus';
import { MonthlyCalendar } from './MonthlyCalendar';
import { SendCustomMessageModal } from '../ui/SendCustomMessageModal';
import { RequestBookingModal } from './RequestBookingModal';
import { SuiviExtrasParPoste } from '../Extras/SuiviExtrasParPoste';
import { BookExtrasModal } from './BookExtrasModal';
import { BookingEmailSettingsModal } from './BookingEmailSettingsModal';
import { Button } from '../ui/Button';
import { LinkedNotesPanel } from '../Notes/LinkedNotesPanel';

interface Calendar {
    id: string;
    month: number;
    year: number;
    status: 'OPEN' | 'ARCHIVED';
    created_at: string;
}

const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

export const EvenementielModule = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    // Le bouton « Menus » mène à la configuration des menus d'une privatisation : il n'a rien à faire là
    // quand le module Menus & Convives est coupé chez ce client. Signalé par Emeni (2026-08-25) — le lien
    // était masqué dans la barre latérale mais ce bouton, lui, restait un passage ouvert. Le serveur refuse
    // désormais aussi ces routes (moduleGate) : masquer un bouton ne désactive rien à lui seul.
    const [menusConvivesActif, setMenusConvivesActif] = React.useState(true);
    React.useEffect(() => {
        authApi.getMyModules()
            .then((modules: any) => {
                const actifs = (Array.isArray(modules) ? modules : []).filter((m: any) => m.is_active === 1).map((m: any) => m.module_name);
                // Un établissement jamais configuré ne renvoie aucun module : on n'affiche pas moins qu'avant
                // dans ce cas, même repli que le verrou serveur.
                setMenusConvivesActif(actifs.length === 0 || actifs.includes('menus_convives'));
            })
            .catch(() => setMenusConvivesActif(true));
    }, []);
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [selectedCalendar, setSelectedCalendar] = useState<Calendar | null>(null);
    const [events, setEvents] = useState<any[]>([]);
    const [linkedNoteEventIds, setLinkedNoteEventIds] = useState<Set<string>>(new Set());
    useEffect(() => {
        const ids = events.map((e) => e.id);
        notesApi.getLinkedIds('event', ids).then((r: any) => setLinkedNoteEventIds(new Set(r.ids || []))).catch(() => {});
    }, [events]);
    const [loading, setLoading] = useState(true);
    const [showEventForm, setShowEventForm] = useState(false);
    const [isBookerMode, setIsBookerMode] = useState(false);
    // Modale dédiée au booking des extras (demande d'Emeni : "Booker" ne doit plus ouvrir 'Modifier la
    // Privatisation') — remplace les anciens déclenchements de EventForm en isBookerMode=true.
    const [bookExtrasTarget, setBookExtrasTarget] = useState<any | null>(null);
    const [showNewCalendarModal, setShowNewCalendarModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
    const [selectedEventCollecte, setSelectedEventCollecte] = useState<{ status: string; menus?: { id: string; name: string }[] } | null>(null);
    const [editingEvent, setEditingEvent] = useState<any | null>(null);
    const [showSendMessage, setShowSendMessage] = useState(false);
    useEffect(() => { setShowSendMessage(false); }, [selectedEvent?.id]);
    useEffect(() => {
        if (!selectedEvent) { setSelectedEventCollecte(null); return; }
        moduleApi.getEvenementielCollecte(selectedEvent.id).then(setSelectedEventCollecte).catch(() => setSelectedEventCollecte(null));
    }, [selectedEvent?.id]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showExtrasPositionsModal, setShowExtrasPositionsModal] = useState(false);
    const [showBookingEmailSettings, setShowBookingEmailSettings] = useState(false);
    const [staffTypes, setStaffTypes] = useState<any[]>([]);
    const [employees, setEmployees] = useState<any[]>([]);
    const [mappingByCategory, setMappingByCategory] = useState<Record<string, string[]>>({});
    const [trackTakenBy, setTrackTakenBy] = useState(false);
    const [allowedTakerIds, setAllowedTakerIds] = useState<string[]>([]);
    const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
    const [showTakerSelector, setShowTakerSelector] = useState(false);
    const [takerSearch, setTakerSearch] = useState('');
    const [mappingSearch, setMappingSearch] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [savingMappings, setSavingMappings] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [newCalendar, setNewCalendar] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
    const [configSpaces, setConfigSpaces] = useState<{ id: string; name: string; color: string }[]>([]);
    const [calendarMenuPos, setCalendarMenuPos] = useState<{ id: string; x: number; y: number } | null>(null);
    const [confirmAction, setConfirmAction] = useState<{ type: 'deleteCalendar' | 'archiveCalendar' | 'deleteEvent'; id: string; label: string } | null>(null);
    const [calendarSection, setCalendarSection] = useState<'active' | 'archives'>('active');
    const [openArchiveYears, setOpenArchiveYears] = useState<Record<number, boolean>>({});
    const [showInfoModal, setShowInfoModal] = useState(true);
    // Délégation d'une demande de booking d'extras à un autre compte (demande d'Emeni) — la logique de
    // booking elle-même (bouton "Booker" existant) n'est pas touchée, ceci ajoute juste une manière d'en
    // envoyer la demande à quelqu'un d'autre par email, pour un événement précis ou tout un calendrier.
    const [requestBookingTarget, setRequestBookingTarget] = useState<{ type: 'event'; id: string; label: string } | { type: 'calendar'; id: string; label: string } | null>(null);
    const [pendingBookEventId, setPendingBookEventId] = useState<string | null>(null);
    const [pendingBookCalendarId, setPendingBookCalendarId] = useState<string | null>(null);

    // Lien reçu par email (POST /evenementiel/:id/request-booking) : ramène directement sur l'événement,
    // Booker ouvert — pas besoin de le retrouver à la main dans le calendrier.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const bookEventId = params.get('bookEvent');
        const bookCalendarId = params.get('bookCalendar');
        if (!bookEventId && !bookCalendarId) return;
        if (bookEventId) setPendingBookEventId(bookEventId);
        navigate('/evenementiel', { replace: true });
        if (bookEventId) {
            moduleApi.getEvenementielEvent(bookEventId).then((ev: any) => {
                if (ev?.calendar_id) moduleApi.getEvenementielCalendars().then((cals: any) => {
                    const cal = Array.isArray(cals) ? cals.find((c: Calendar) => c.id === ev.calendar_id) : null;
                    if (cal) setSelectedCalendar(cal);
                });
            }).catch(() => {});
        } else if (bookCalendarId) {
            setPendingBookCalendarId(bookCalendarId);
            moduleApi.getEvenementielCalendars().then((cals: any) => {
                const cal = Array.isArray(cals) ? cals.find((c: Calendar) => c.id === bookCalendarId) : null;
                if (cal) setSelectedCalendar(cal);
            }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Dès que les événements du calendrier ciblé sont chargés, ouvre directement Booker sur l'événement
    // demandé (au lieu de forcer la personne à le retrouver elle-même dans la liste) — mais seulement si le
    // compte connecté est bien le destinataire de la demande (demande d'Emeni : "la personne doit être
    // connectée sur le compte concerné"), sinon le lien ne doit rien ouvrir pour un autre compte.
    useEffect(() => {
        if (!pendingBookEventId) return;
        const ev = events.find((e: any) => e.id === pendingBookEventId);
        if (!ev) return;
        setPendingBookEventId(null);
        const br = ev.booking_request;
        const isRecipient = !!br && !br.completed_notified_at && user && br.recipient_type === user.type && br.recipient_id === user.id;
        if (isRecipient) {
            setBookExtrasTarget(ev);
            return;
        }
        // Le demandeur d'origine (celui qui a envoyé "Demander à booker") reçoit aussi ce lien dans l'email
        // de confirmation ("extras internes modifiés") — il ne peut pas booker à la place du destinataire,
        // mais doit pouvoir consulter la privatisation, pas se faire bloquer comme un compte non concerné.
        const isRequester = !!br && user && br.requester_type === user.type && br.requester_id === user.id;
        if (isRequester) {
            setSelectedEvent(ev);
            return;
        }
        setToastMessage("Cette demande de booking est adressée à un autre compte — connecte-toi avec le bon compte pour continuer.");
        setTimeout(() => setToastMessage(''), 4000);
    }, [events, pendingBookEventId, user]);

    // Même garde pour le lien "calendrier entier" : si le compte connecté n'est destinataire d'aucune
    // demande en attente sur ce calendrier, on le signale plutôt que de laisser le calendrier s'ouvrir
    // silencieusement comme si de rien n'était.
    useEffect(() => {
        if (!pendingBookCalendarId || !selectedCalendar || selectedCalendar.id !== pendingBookCalendarId) return;
        if (!events.length) return;
        setPendingBookCalendarId(null);
        const hasPendingForMe = events.some((ev: any) => {
            const br = ev.booking_request;
            return !!br && !br.completed_notified_at && user && br.recipient_type === user.type && br.recipient_id === user.id;
        });
        if (!hasPendingForMe) {
            setToastMessage("Cette demande de booking est adressée à un autre compte — connecte-toi avec le bon compte pour continuer.");
            setTimeout(() => setToastMessage(''), 4000);
        }
    }, [events, pendingBookCalendarId, selectedCalendar, user]);

    const normalizeIdList = (value: any): string[] => {
        if (!Array.isArray(value)) return [];
        return value
            .map((id) => String(id))
            .filter((id) => id.trim().length > 0);
    };

    // Le bouton du calendrier bascule en "Notifier le calendrier est à jour" dès que le compte connecté est
    // destinataire d'au moins une demande de booking encore en attente sur ce calendrier (demande d'Emeni :
    // "celui qui booke, le bouton se transforme").
    const calendarHasPendingBookingForMe = events.some((ev: any) => {
        const br = ev.booking_request;
        return !!br && !br.completed_notified_at && user && br.recipient_type === user.type && br.recipient_id === user.id;
    });

    const handleNotifyCalendarBookingDone = async () => {
        if (!selectedCalendar) return;
        try {
            await moduleApi.notifyEvenementielCalendarBookingDone(selectedCalendar.id);
            loadEvents(selectedCalendar.id);
            setToastMessage('Demandeur(s) notifié(s).');
            setTimeout(() => setToastMessage(''), 2500);
        } catch (e: any) {
            setToastMessage(e?.message || "Erreur lors de l'envoi.");
            setTimeout(() => setToastMessage(''), 3000);
        }
    };

    const activeCalendars = calendars.filter((c) => c.status !== 'ARCHIVED');
    const archivedCalendars = calendars.filter((c) => c.status === 'ARCHIVED');
    const hasArchivedCalendars = archivedCalendars.length > 0;
    const archivesByYear = archivedCalendars.reduce<Record<number, Calendar[]>>((acc, cal) => {
        if (!acc[cal.year]) acc[cal.year] = [];
        acc[cal.year].push(cal);
        return acc;
    }, {});
    const archiveYears = Object.keys(archivesByYear)
        .map((y) => Number(y))
        .sort((a, b) => b - a);

    useEffect(() => {
        loadCalendars();
    }, []);

    useEffect(() => {
        if (user?.id) {
            loadConfigSpaces();
        }
    }, [user?.id]);

    useEffect(() => {
        if (selectedCalendar) {
            loadEvents(selectedCalendar.id);
        }
    }, [selectedCalendar]);

    useEffect(() => {
        if (showSettingsModal) {
            loadStaffMappings();
        }
    }, [showSettingsModal]);

    useEffect(() => {
        if (!calendarMenuPos) return;
        const close = () => setCalendarMenuPos(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [calendarMenuPos]);

    useEffect(() => {
        if (calendarSection === 'archives' && !hasArchivedCalendars) {
            setCalendarSection('active');
        }
    }, [calendarSection, hasArchivedCalendars]);

    const loadCalendars = async () => {
        setLoading(true);
        try {
            const data = await moduleApi.getEvenementielCalendars();
            setCalendars(data);
            if (data.length > 0) {
                const refreshedSelected = selectedCalendar ? data.find((c: Calendar) => c.id === selectedCalendar.id) : null;
                if (refreshedSelected) {
                    setSelectedCalendar(refreshedSelected);
                    setCalendarSection(refreshedSelected.status === 'ARCHIVED' ? 'archives' : 'active');
                } else {
                    const defaultCalendar = data.find((c: Calendar) => c.status !== 'ARCHIVED') || data[0];
                    setSelectedCalendar(defaultCalendar);
                    setCalendarSection(defaultCalendar.status === 'ARCHIVED' ? 'archives' : 'active');
                }
            } else {
                setSelectedCalendar(null);
                setCalendarSection('active');
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const loadEvents = async (id: string) => {
        try {
            const data = await moduleApi.getEvenementielCalendarEvents(id);
            setEvents(data);
        } catch (e) {
            console.error(e);
        }
    };

    const loadConfigSpaces = async () => {
        try {
            const [config, directSpaces, emps] = await Promise.all([
                moduleApi.getEvenementielConfig(),
                moduleApi.getEvenementielSpaces().catch(() => []),
                moduleApi.getEmployes().catch(() => [])
            ]);

            const fromConfig = Array.isArray(config?.spaces) ? config.spaces : [];
            const fromDirect = Array.isArray(directSpaces) ? directSpaces : [];
            const merged = fromConfig.length > 0 ? fromConfig : fromDirect;

            const spaces = merged.map((s: any) => ({
                id: String(s.id),
                name: String(s.name || 'Espace'),
                color: String(s.color_hex || s.color || '#3b82f6'),
            }));
            setConfigSpaces(spaces);
            setEmployees(Array.isArray(emps) ? emps : []);
        } catch (e) {
            console.error('Erreur chargement espaces config:', e);
        }
    };

    const handleDeleteCalendar = async (id: string) => {
        try {
            await moduleApi.deleteEvenementielCalendar(id);
            setConfirmAction(null);
            if (selectedCalendar?.id === id) setSelectedCalendar(null);
            loadCalendars();
            setToastMessage('Calendrier supprimé.');
            setTimeout(() => setToastMessage(''), 2500);
        } catch (e: any) {
            setToastMessage(e?.message || 'Erreur lors de la suppression.');
            setTimeout(() => setToastMessage(''), 3000);
        }
    };

    const handleToggleArchiveCalendar = async (id: string) => {
        try {
            const result = await moduleApi.archiveEvenementielCalendar(id);
            setConfirmAction(null);
            loadCalendars();
            setToastMessage(result?.status === 'ARCHIVED' ? 'Calendrier archivé.' : 'Calendrier réouvert.');
            setTimeout(() => setToastMessage(''), 2500);
        } catch (e: any) {
            setToastMessage(e?.message || 'Erreur lors de l\'archivage.');
            setTimeout(() => setToastMessage(''), 3000);
        }
    };

    const handleDeleteEvent = async (id: string) => {
        try {
            await moduleApi.deleteEvenementiel(id);
            setConfirmAction(null);
            setSelectedEvent(null);
            if (selectedCalendar) loadEvents(selectedCalendar.id);
            setToastMessage('Privatisation supprimée.');
            setTimeout(() => setToastMessage(''), 2500);
        } catch (e: any) {
            setToastMessage(e?.message || 'Erreur lors de la suppression.');
            setTimeout(() => setToastMessage(''), 3000);
        }
    };

    const loadStaffMappings = async () => {
        setSettingsLoading(true);
        setSettingsError('');
        try {
            const [config, emps, mappings] = await Promise.all([
                moduleApi.getEvenementielConfig(),
                moduleApi.getEmployes(),
                moduleApi.getStaffCategoryMappings()
            ]);

            const staff = config?.authorized_staff_categories || [];

            setStaffTypes(staff);
            setEmployees(emps);
            setTrackTakenBy(!!config?.track_taken_by);
            setAllowedTakerIds(normalizeIdList(config?.allowed_taker_employee_ids));

            const grouped: Record<string, string[]> = {};
            staff.forEach((category: any) => {
                grouped[category.id] = [];
            });
            (mappings || []).forEach((row: any) => {
                if (!grouped[row.staff_category_id]) {
                    grouped[row.staff_category_id] = [];
                }
                grouped[row.staff_category_id].push(row.employee_id);
            });
            setMappingByCategory(grouped);
        } catch (e: any) {
            setSettingsError(e?.message || 'Impossible de charger le mapping staff.');
        } finally {
            setSettingsLoading(false);
        }
    };

    const employeeName = (employee: any) => {
        const fullName = [employee.first_name, employee.last_name].filter(Boolean).join(' ').trim();
        return fullName || employee.name || employee.email || employee.id;
    };

    const toggleMappedEmployee = (staffCategoryId: string, employeeId: string) => {
        setMappingByCategory(prev => {
            const current = prev[staffCategoryId] || [];
            const next = current.includes(employeeId)
                ? current.filter(id => id !== employeeId)
                : [...current, employeeId];
            return { ...prev, [staffCategoryId]: next };
        });
    };

    const handleSaveMappings = async () => {
        if (user?.type === 'admin') {
            setSettingsError('En mode superadmin, ouvrez ce module via "Se connecter" sur un client pour enregistrer ses paramètres.');
            return;
        }
        setSavingMappings(true);
        setSettingsError('');
        try {
            const payload = staffTypes.map((category: any) => ({
                staff_category_id: category.id,
                employee_ids: mappingByCategory[category.id] || []
            }));

            // Save both staff mappings and config
            await Promise.all([
                moduleApi.saveStaffCategoryMappings(payload),
                moduleApi.saveEvenementielConfig({
                    track_taken_by: trackTakenBy,
                    allowed_taker_employee_ids: allowedTakerIds,
                })
            ]);

            setShowSettingsModal(false);
            setToastMessage('Paramètres enregistrés avec succès.');
            setTimeout(() => setToastMessage(''), 2800);
        } catch (e: any) {
            console.error('Save Mapping Error:', e);
            setSettingsError(e?.message || 'Impossible d\'enregistrer le mapping staff.');
        } finally {
            setSavingMappings(false);
        }
    };

    const toggleAllowedTaker = (employeeId: string) => {
        setAllowedTakerIds((prev) => (
            prev.includes(employeeId)
                ? prev.filter((id) => id !== employeeId)
                : [...prev, employeeId]
        ));
    };

    const filteredMappingEmployees = employees.filter((emp: any) => {
        const term = mappingSearch.trim().toLowerCase();
        if (!term) return true;
        const fullName = employeeName(emp).toLowerCase();
        const email = String(emp.email || '').toLowerCase();
        return fullName.includes(term) || email.includes(term);
    });

    const filteredTakerEmployees = employees.filter((emp: any) => {
        const term = takerSearch.trim().toLowerCase();
        if (!term) return true;
        const fullName = employeeName(emp).toLowerCase();
        const email = String(emp.email || '').toLowerCase();
        return fullName.includes(term) || email.includes(term);
    });

    const handleCreateCalendar = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await moduleApi.createEvenementielCalendar(newCalendar);
            setShowNewCalendarModal(false);
            loadCalendars();
        } catch (e: any) {
            alert(e.message);
        }
    };

    const handleMonthChange = (month: number, year: number) => {
        const cal = calendars.find(c => c.month === month && c.year === year);
        if (cal) {
            setSelectedCalendar(cal);
        } else {
            setNewCalendar({ month, year });
            setShowNewCalendarModal(true);
        }
    };

    return (
        <div className="space-y-6">
            <header className="no-print flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight">Module Événementiel</h1>
                    <p className="text-content-muted text-sm mt-0.5">
                        Gérez vos privatisations et votre staffing
                        {selectedCalendar && (
                            <span className="ml-2 text-[10px] bg-surface-soft px-2 py-0.5 rounded border border-border">
                                Créé le {new Date(selectedCalendar.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    {user?.type !== 'admin' && (
                        <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                            <Settings size={15} />
                            <span>Paramètres</span>
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => setShowNewCalendarModal(true)}>
                        <CalendarIcon size={15} />
                        <span>Nouveau Calendrier</span>
                    </Button>
                    {selectedCalendar && selectedCalendar.status === 'OPEN' && (
                        <Button onClick={() => {
                            setEditingEvent(null);
                            setShowEventForm(true);
                        }}>
                            <Plus size={15} />
                            <span>Nouvelle Privatisation</span>
                        </Button>
                    )}
                </div>
            </header>

            {/* Calendar Sections */}
            <div className="no-print space-y-4">
                <div className="flex items-center gap-2 bg-[var(--bg-card)] p-1 rounded-xl border border-[var(--border-color)] w-fit">
                    <button
                        onClick={() => setCalendarSection('active')}
                        className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${calendarSection === 'active' ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`}
                    >
                        Calendriers Actifs
                    </button>
                    {hasArchivedCalendars && (
                        <button
                            onClick={() => setCalendarSection('archives')}
                            className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${calendarSection === 'archives' ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`}
                        >
                            Archives
                        </button>
                    )}
                </div>

                {calendarSection === 'active' && (
                    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                        {activeCalendars.map((cal) => (
                            <div key={cal.id} className="flex-shrink-0">
                                <div className={`flex items-center rounded-xl border transition-all text-xs font-bold overflow-hidden ${
                                    selectedCalendar?.id === cal.id
                                        ? 'bg-brand-600 border-brand-600 text-brand-fg shadow-md'
                                        : 'bg-[var(--bg-card)] border-[var(--border-color)] text-[var(--text-muted)] hover:border-brand-400 hover:text-brand-600 dark:hover:text-brand-400'
                                }`}>
                                    <button
                                        onClick={() => {
                                            setSelectedCalendar(cal);
                                            setCalendarSection('active');
                                        }}
                                        className="px-4 py-2"
                                    >
                                        {MONTHS[cal.month - 1]} {cal.year}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (calendarMenuPos?.id === cal.id) {
                                                setCalendarMenuPos(null);
                                            } else {
                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                setCalendarMenuPos({ id: cal.id, x: rect.left, y: rect.bottom + 4 });
                                            }
                                        }}
                                        className={`px-2 py-2 border-l transition-all ${
                                            selectedCalendar?.id === cal.id ? 'border-transparent hover:bg-black/10 dark:hover:bg-white/10' : 'border-[var(--border-color)] hover:bg-brand-600/10'
                                        }`}
                                    >
                                        <MoreHorizontal size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {activeCalendars.length === 0 && (
                            <div className="text-xs text-content-muted px-2 py-3">Aucun calendrier actif.</div>
                        )}
                    </div>
                )}

                {calendarSection === 'archives' && hasArchivedCalendars && (
                    <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-4 space-y-3">
                        {archiveYears.map((year) => {
                            const yearCalendars = [...archivesByYear[year]].sort((a, b) => a.month - b.month);
                            const isOpen = openArchiveYears[year] ?? true;
                            return (
                                <div key={year} className="border border-[var(--border-color)] rounded-xl overflow-hidden">
                                    <button
                                        onClick={() => setOpenArchiveYears((prev) => ({ ...prev, [year]: !isOpen }))}
                                        className="w-full flex items-center justify-between px-4 py-3 bg-[var(--interactive-hover)] hover:brightness-95 transition-all"
                                    >
                                        <div className="flex items-center gap-2">
                                            {isOpen ? <FolderOpen size={16} className="text-warning-400" /> : <Folder size={16} className="text-warning-400" />}
                                            <span className="text-sm font-bold text-[var(--text-primary)]">{year}</span>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--interactive-hover)] text-[var(--text-muted)]">{yearCalendars.length}</span>
                                        </div>
                                        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="p-2 space-y-1 bg-[var(--bg-card)]">
                                            {yearCalendars.map((cal) => (
                                                <div key={cal.id} className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedCalendar(cal);
                                                            setCalendarSection('archives');
                                                        }}
                                                        className={`flex-1 text-left px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                                                            selectedCalendar?.id === cal.id
                                                                ? 'bg-brand-600 text-brand-fg shadow-sm'
                                                                : 'bg-transparent text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'
                                                        }`}
                                                    >
                                                        {MONTHS[cal.month - 1]} {cal.year}
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (calendarMenuPos?.id === cal.id) {
                                                                setCalendarMenuPos(null);
                                                            } else {
                                                                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                                setCalendarMenuPos({ id: cal.id, x: rect.left, y: rect.bottom + 4 });
                                                            }
                                                        }}
                                                        className="px-2 py-2 rounded-lg border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--interactive-hover)]"
                                                    >
                                                        <MoreHorizontal size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Dropdown menu calendrier (fixed pour échapper au overflow-x-auto) */}
            {calendarMenuPos && (() => {
                const cal = calendars.find(c => c.id === calendarMenuPos.id);
                if (!cal) return null;
                return (
                    <div
                        className="fixed z-50 w-44 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-2xl overflow-hidden"
                        style={{ top: calendarMenuPos.y, left: calendarMenuPos.x }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => {
                                setCalendarMenuPos(null);
                                handleToggleArchiveCalendar(cal.id);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all"
                        >
                            <Archive size={13} />
                            {cal.status === 'ARCHIVED' ? 'Désarchiver' : 'Archiver'}
                        </button>
                        <button
                            onClick={() => {
                                setCalendarMenuPos(null);
                                setConfirmAction({ type: 'deleteCalendar', id: cal.id, label: `${MONTHS[cal.month - 1]} ${cal.year}` });
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-red-400 hover:bg-red-500/10 transition-all"
                        >
                            <Trash2 size={13} />
                            Supprimer
                        </button>
                    </div>
                );
            })()}

            {/* Main Calendar View */}
            {selectedCalendar ? (
                <MonthlyCalendar 
                    month={selectedCalendar.month}
                    year={selectedCalendar.year}
                    events={events}
                    configSpaces={configSpaces}
                    onMonthChange={handleMonthChange}
                    onEventClick={setSelectedEvent}
                    onRequestCalendarBooking={user?.type !== 'admin' ? () => setRequestBookingTarget({ type: 'calendar', id: selectedCalendar.id, label: `${MONTHS[selectedCalendar.month - 1]} ${selectedCalendar.year}` }) : undefined}
                    calendarBookingPending={calendarHasPendingBookingForMe}
                    onNotifyCalendarBookingDone={user?.type !== 'admin' ? handleNotifyCalendarBookingDone : undefined}
                    onRequestBooking={user?.type !== 'admin' ? (ev) => setRequestBookingTarget({ type: 'event', id: ev.id, label: ev.type === 'PRIVÉ' ? `${ev.first_name || ''} ${ev.last_name || ''}`.trim() : (ev.company_name || '') }) : undefined}
                    onBookExtras={user?.type !== 'admin' ? (ev) => setBookExtrasTarget(ev) : undefined}
                    linkedNoteEventIds={linkedNoteEventIds}
                />
            ) : (
                <div className="py-40 text-center bg-[var(--bg-card)] rounded-3xl border border-dashed border-[var(--border-color)]">
                    <CalendarIcon size={48} className="text-gray-700 mx-auto mb-4" />
                    <p className="text-[var(--text-muted)] font-medium">Sélectionnez ou créez un calendrier pour commencer</p>
                </div>
            )}

            {/* Modals */}
            <AnimatePresence>
                {showEventForm && selectedCalendar && (
                    <EventForm
                        calendarId={selectedCalendar.id}
                        month={selectedCalendar.month}
                        year={selectedCalendar.year}
                        initialEvent={editingEvent}
                        isBookerMode={isBookerMode}
                        onClose={() => {
                            setShowEventForm(false);
                            setEditingEvent(null);
                            setIsBookerMode(false);
                        }}
                        onSuccess={() => {
                            setShowEventForm(false);
                            setEditingEvent(null);
                            setIsBookerMode(false);
                            loadEvents(selectedCalendar.id);
                        }}
                    />
                )}

                {showSettingsModal && (
                    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-6 overflow-y-auto">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-5xl bg-[var(--bg-card)] rounded-3xl border border-[var(--border-color)] shadow-2xl overflow-hidden"
                        >
                            <div className="p-8 border-b border-[var(--border-color)] bg-[var(--interactive-hover)] flex items-center justify-between">
                                <div>
                                    <h2 className="text-2xl font-bold text-[var(--text-primary)]">Paramètres Événementiel</h2>
                                    <p className="text-sm text-[var(--text-muted)] mt-1">Mapping du Staff entre catégories autorisées et employés internes.</p>
                                </div>
                                <button onClick={() => setShowSettingsModal(false)} className="p-2 rounded-full text-[var(--text-muted)] hover:bg-[var(--interactive-hover)]">
                                    <X size={22} />
                                </button>
                            </div>

                            <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto">
                                {settingsError && (
                                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                                        {settingsError}
                                    </div>
                                )}

                                <section className="space-y-3">
                                    <h3 className="text-sm font-bold uppercase tracking-widest text-content-muted">Extras</h3>
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="outline" onClick={() => setShowExtrasPositionsModal(true)}>
                                            <Briefcase size={15} />
                                            <span>Extras internes</span>
                                        </Button>
                                        <Button variant="outline" onClick={() => navigate('/extras-partenaires')}>
                                            <Briefcase size={15} />
                                            <span>Extras externes</span>
                                        </Button>
                                        <Button variant="outline" onClick={() => setShowBookingEmailSettings(true)}>
                                            <Send size={15} />
                                            <span>Emails booking extras</span>
                                        </Button>
                                    </div>
                                </section>

                                <section className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-bold uppercase tracking-widest text-content-muted">Mapping du Staff</h3>
                                        <span className="text-xs text-content-muted">{staffTypes.length} catégories</span>
                                    </div>

                                    {settingsLoading ? (
                                        <div className="p-6 rounded-2xl border border-border text-content-muted">Chargement...</div>
                                    ) : staffTypes.length === 0 ? (
                                        <div className="p-6 rounded-2xl border border-border text-content-muted">
                                            Aucune catégorie de staff disponible pour ce client.
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {staffTypes.map((category: any) => {
                                                const selectedIds = mappingByCategory[category.id] || [];
                                                const selectedEmployees = employees.filter((emp: any) => selectedIds.includes(emp.id));

                                                return (
                                                    <div key={category.id} className="bg-surface-soft border border-border rounded-2xl p-5 space-y-4">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <p className="text-content font-bold">{category.name}</p>
                                                                <p className="text-xs text-content-muted">Choisissez les employés autorisés pour cette catégorie.</p>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setMappingSearch('');
                                                                    setOpenCategoryId(openCategoryId === category.id ? null : category.id);
                                                                }}
                                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-sm"
                                                            >
                                                                <span>Sélectionner</span>
                                                                <ChevronDown size={14} className={`${openCategoryId === category.id ? 'rotate-180' : ''} transition-transform`} />
                                                            </button>
                                                        </div>

                                                        {openCategoryId === category.id && (
                                                            <div className="bg-surface border border-border rounded-xl p-3 max-h-52 overflow-y-auto space-y-2">
                                                                <div className="relative mb-2">
                                                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                                                                    <input
                                                                        value={mappingSearch}
                                                                        onChange={(e) => setMappingSearch(e.target.value)}
                                                                        placeholder="Rechercher un employé..."
                                                                        className="w-full bg-surface border border-border rounded-lg pl-8 pr-3 py-2 text-xs text-content outline-none focus:border-border"
                                                                    />
                                                                </div>
                                                                {filteredMappingEmployees.length === 0 && (
                                                                    <p className="text-xs text-content-muted">Aucun employé correspondant.</p>
                                                                )}
                                                                {filteredMappingEmployees.map((emp: any) => {
                                                                    const selected = selectedIds.includes(emp.id);
                                                                    return (
                                                                        <button
                                                                            key={emp.id}
                                                                            type="button"
                                                                            onClick={() => toggleMappedEmployee(category.id, emp.id)}
                                                                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left text-sm transition-all ${
                                                                                selected
                                                                                    ? 'bg-white text-slate-900 border-white'
                                                                                    : 'bg-surface border-border text-content-muted hover:bg-surface-soft'
                                                                            }`}
                                                                        >
                                                                            <span>{employeeName(emp)}</span>
                                                                            {selected && <Check size={14} />}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        <div className="flex flex-wrap gap-2">
                                                            {selectedEmployees.length === 0 ? (
                                                                <span className="text-xs text-content-muted">Aucun employé sélectionné.</span>
                                                            ) : selectedEmployees.map((emp: any) => (
                                                                <span key={`${category.id}-${emp.id}`} className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-200 dark:bg-surface-soft border border-slate-300 dark:border-border text-xs text-content font-medium">
                                                                    {employeeName(emp)}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </section>

                                <section className="space-y-4 pt-4 border-t border-border">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-bold uppercase tracking-widest text-content-muted">Suivi Pris Par</h3>
                                            <p className="text-xs text-content-muted mt-1">Active la sélection d'un preneur lors de la création d'une privatisation.</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setTrackTakenBy((prev) => !prev)}
                                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${trackTakenBy ? 'border-green-400/40 bg-green-500/10 text-green-300' : 'border-slate-300 dark:border-border text-content-muted hover:bg-surface-soft'}`}
                                        >
                                            {trackTakenBy ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                            <span className="text-xs font-bold">Activer le suivi 'Pris par'</span>
                                        </button>
                                    </div>

                                    {trackTakenBy && (
                                        <div className="bg-surface-soft border border-border rounded-2xl p-5 space-y-4">
                                            <div className="flex items-center justify-between">
                                                <p className="text-content font-bold">Employés autorisés comme "Preneur"</p>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowTakerSelector((prev) => !prev)}
                                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-content-muted hover:bg-surface-soft text-xs font-bold"
                                                >
                                                    <span>Sélectionner</span>
                                                    <ChevronDown size={14} className={`${showTakerSelector ? 'rotate-180' : ''} transition-transform`} />
                                                </button>
                                            </div>

                                            {showTakerSelector && (
                                                <div className="bg-surface border border-border rounded-xl p-3 max-h-52 overflow-y-auto space-y-2">
                                                    <div className="relative mb-2">
                                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                                                        <input
                                                            value={takerSearch}
                                                            onChange={(e) => setTakerSearch(e.target.value)}
                                                            placeholder="Rechercher un employé..."
                                                            className="w-full bg-surface border border-border rounded-lg pl-8 pr-3 py-2 text-xs text-content outline-none focus:border-border"
                                                        />
                                                    </div>
                                                    {filteredTakerEmployees.length === 0 && <p className="text-xs text-content-muted">Aucun employé disponible.</p>}
                                                    {filteredTakerEmployees.map((emp: any) => {
                                                        const selected = allowedTakerIds.includes(emp.id);
                                                        return (
                                                            <button
                                                                key={`taker-${emp.id}`}
                                                                type="button"
                                                                onClick={() => toggleAllowedTaker(emp.id)}
                                                                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-left text-sm transition-all ${selected ? 'bg-white text-slate-900 border-white' : 'bg-surface border-border text-content-muted hover:bg-surface-soft'}`}
                                                            >
                                                                <span>{employeeName(emp)}</span>
                                                                {selected && <Check size={14} />}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            <div className="flex flex-wrap gap-2">
                                                {allowedTakerIds.length === 0 ? (
                                                    <span className="text-xs text-content-muted">Aucun preneur autorisé sélectionné.</span>
                                                ) : employees
                                                    .filter((emp: any) => allowedTakerIds.includes(emp.id))
                                                    .map((emp: any) => (
                                                        <span key={`badge-taker-${emp.id}`} className="inline-flex items-center px-3 py-1.5 rounded-full bg-slate-200 dark:bg-surface-soft border border-slate-300 dark:border-border text-xs text-content font-medium">
                                                            {employeeName(emp)}
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </section>

                            </div>

                            <div className="p-6 border-t border-[var(--border-color)] bg-[var(--interactive-hover)] flex justify-end gap-3">
                                <button
                                    onClick={() => setShowSettingsModal(false)}
                                    className="px-5 py-3 rounded-xl font-bold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--interactive-hover)]"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleSaveMappings}
                                    disabled={savingMappings || settingsLoading}
                                    className="px-5 py-3 rounded-xl font-bold bg-[var(--text-primary)] text-[var(--bg-card)] hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                                >
                                    <Save size={16} />
                                    <span>{savingMappings ? 'Enregistrement...' : 'Enregistrer le mapping'}</span>
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {selectedEvent && (
                    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-6">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-2xl max-h-[90vh] bg-[var(--bg-card)] rounded-3xl border border-[var(--border-color)] overflow-hidden shadow-2xl flex flex-col"
                        >
                            <div className="p-4 md:p-8 border-b border-[var(--border-color)] flex items-center justify-between bg-[var(--interactive-hover)] shrink-0">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[var(--interactive-hover)] rounded-2xl flex items-center justify-center border border-[var(--border-color)]">
                                        {selectedEvent.type === 'PRIVÉ' ? <User className="text-[var(--text-primary)]" /> : <Building2 className="text-[var(--text-primary)]" />}
                                    </div>
                                    <div>
                                        {/* 1. NOM PRENOM/ENTREPRISE */}
                                        <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                                            {selectedEvent.type === 'PRIVÉ' ? `${selectedEvent.first_name} ${selectedEvent.last_name}` : selectedEvent.company_name}
                                        </h2>
                                        {/* 2. NOMBRE DE PERSONNES */}
                                        <p className="text-[var(--text-muted)] text-sm flex items-center gap-2 mt-1">
                                            <Users size={14} />
                                            {selectedEvent.num_people} personnes • {selectedEvent.type}
                                        </p>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-[var(--interactive-hover)] rounded-full text-[var(--text-muted)]">
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="px-4 md:px-8 pt-4 md:pt-5 flex items-center justify-between gap-3 flex-wrap border-b border-[var(--border-color)] pb-4">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[11px] font-black uppercase px-3 py-1.5 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                                        {COLLECTE_STATUS_LABELS[selectedEventCollecte?.status || 'menus_a_configurer']}
                                    </span>
                                    {selectedEventCollecte?.menus && selectedEventCollecte.menus.length > 0 ? (
                                        selectedEventCollecte.menus.map((m) => (
                                            <span key={m.id} className="text-[11px] font-bold px-3 py-1.5 rounded-lg bg-[var(--interactive-hover)] text-[var(--text-primary)] border border-[var(--border-color)] flex items-center gap-1.5">
                                                <Utensils size={12} /> {m.name}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-[11px] text-[var(--text-muted)] italic">Aucune formule rattachée</span>
                                    )}
                                </div>
                                {menusConvivesActif && (
                                    <Button variant="outline" size="sm" onClick={() => navigate(`/menus-convives/${selectedEvent.id}/menu`)}>
                                        <Utensils size={14} />
                                        <span>Menus</span>
                                    </Button>
                                )}
                            </div>

                            <div className="p-4 md:p-8 space-y-8 overflow-y-auto">
                                {(() => {
                                    const eventDate = new Date(selectedEvent.start_time);
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const isPastEvent = eventDate < today;
                                    const isReadOnly = selectedCalendar?.status === 'ARCHIVED' || isPastEvent;
                                    // Store for use in button area
                                    (selectedEvent as any).__isReadOnly = isReadOnly;
                                    return null;
                                })()}
                                {(selectedCalendar?.status === 'ARCHIVED' || (selectedEvent as any).__isReadOnly) && (
                                    <div className="p-4 bg-warning-500/10 border border-warning-500/20 rounded-xl text-warning-300 text-sm font-medium">
                                        {selectedCalendar?.status === 'ARCHIVED' 
                                            ? 'Ce calendrier est archivé : consultation et impression uniquement (lecture seule).'
                                            : 'Cette privatisation est passée : consultation uniquement (lecture seule).'}
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* 3. HEURE DEBUT ET HEURE FIN */}
                                    <div className="space-y-4">
                                        <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-2">
                                            <Clock size={14} /> Horaires
                                        </h3>
                                        <div className="p-4 bg-[var(--interactive-hover)] rounded-2xl border border-[var(--border-color)]">
                                            <div className="flex items-center justify-between text-[var(--text-primary)]">
                                                <span className="text-[var(--text-muted)] text-xs">Début</span>
                                                <span className="font-bold">{new Date(selectedEvent.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-[var(--text-primary)] mt-2">
                                                <span className="text-[var(--text-muted)] text-xs">Fin</span>
                                                <span className="font-bold">{new Date(selectedEvent.end_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <div className="mt-3 pt-3 border-t border-[var(--border-color)] text-[10px] text-[var(--text-muted)] font-bold uppercase tracking-wider">
                                                {new Date(selectedEvent.start_time).toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-2">
                                            <MapPin size={14} /> Espaces
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedEvent.spaces?.map((s: any) => (
                                                <span key={s.id} className="px-3 py-1.5 rounded-xl bg-[var(--interactive-hover)] text-xs font-bold border border-[var(--border-color)]" style={{ color: s.color }}>
                                                    {s.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Extras bookés — état réel (internes bookés / externes demandés-confirmés / reste
                                    à booker) par type, alimenté par extras_summary déjà calculé côté serveur pour la
                                    bulle du calendrier. Demande d'Emeni : voir dans la fiche détail les extras
                                    bookés sur cette privatisation. */}
                                {/* Suivi des extras, poste par poste. Remplace l'ancienne liste de pastilles
                                    (noms internes + "N externes en attente d'envoi") qui ne disait jamais
                                    COMBIEN il en fallait : on voyait ce qui etait booke, pas ce qui manquait.
                                    Les trois compteurs sont desormais separes — requis / interne / prestataire —
                                    parce que le process passe d'abord par les salaries de la maison et que seul
                                    le reliquat part chez le prestataire. Meme composant que le suivi global :
                                    deux ecrans qui montrent la meme chose doivent le calculer au meme endroit. */}
                                {Array.isArray(selectedEvent.extras_summary) && selectedEvent.extras_summary.length > 0 && (
                                    <div className="space-y-3">
                                        <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-2">
                                            <Users size={14} /> Extras requis
                                        </h3>
                                        <SuiviExtrasParPoste postes={selectedEvent.extras_summary.map((s: any) => ({
                                            typeId: String(s.extra_type_id || s.type_label || ''),
                                            label: s.type_label || '',
                                            requis: Number(s.requis) || 0,
                                            interne: Array.isArray(s.internal_names) ? s.internal_names.length : 0,
                                            commande: Number(s.external_count) || 0,
                                            // Ce qui est parti chez le prestataire : l'attente de confirmation et
                                            // le confirme. Le "en attente d'envoi" n'a pas encore quitte la maison.
                                            envoye: (Number(s.external_awaiting_confirmation) || 0) + (Number(s.external_confirmed) || 0),
                                            confirme: Number(s.external_confirmed) || 0,
                                            tenableEnInterne: !!s.tenable_en_interne,
                                            slots: Array.isArray(s.slots) ? s.slots : [],
                                            internes: Array.isArray(s.internal_names) ? s.internal_names : [],
                                        }))} />
                                    </div>
                                )}

                {/* BESOINS EN PERSONNEL SAISIS AVANT LE MODULE EXTRAS.
                    Cette section avait ete retiree le 22/08 comme "redondante avec le systeme Extras". Elle
                    l'est pour une privatisation creee depuis, mais PAS pour celles d'avant : Brasserie Polpo
                    avait 15 privatisations et 43 personnes saisies de cette facon, plus 13 affectations
                    nominatives. En retirant l'ecran, on a rendu ces donnees invisibles du jour au lendemain —
                    signale par le client le 27/08, le matin de la mise en production.
                    Les donnees n'ont jamais ete supprimees : elles etaient simplement devenues inaffichables.

                    Difference avec la version d'origine : la section ne s'affiche QUE s'il y a reellement
                    quelque chose a montrer. Un etablissement qui n'a jamais utilise l'ancien systeme ne voit
                    donc rien, ce qui repond au motif du retrait initial sans sacrifier l'historique des
                    autres. */}
                                {Array.isArray((selectedEvent as any).staff) && (selectedEvent as any).staff.length > 0 && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Besoins en personnel</h3>
                                        <p className="text-[10px] text-[var(--text-muted)] mt-1 italic">
                                            Saisis avant la mise en place des Partenaires &amp; Prestataires. Conserves tels quels.
                                        </p>
                                    </div>
                                    <div className="space-y-4">
                                        {(selectedEvent as any).staff.map((s: any, idx: number) => {
                                            const assignments = (selectedEvent as any).assignments?.filter((a: any) => a.staff_type_id === s.staff_type_id) || [];
                                            const extras = Math.max(0, s.count - assignments.length);
                                            // `name` est desormais renvoye par le serveur (jointure sur les types
                                            // de personnel). Repli sur la liste chargee par l'ecran, puis sur un
                                            // libelle explicite si le poste a reellement ete supprime depuis.
                                            const nomType = s.name || staffTypes.find((t: any) => t.id === s.staff_type_id)?.name || 'Poste supprimé';

                                            return (
                                                <div key={idx} className="bg-[var(--interactive-hover)] rounded-2xl p-6 border border-[var(--border-color)] space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-bold text-[var(--text-primary)] text-lg">{nomType}</span>
                                                        <div className="flex items-center gap-2 px-3 py-1 bg-[var(--interactive-hover)] rounded-lg text-xs font-bold text-[var(--text-muted)]">
                                                            Besoin: {s.count}
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div className="space-y-2">
                                                            <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Internes Booker</p>
                                                            <div className="flex flex-wrap gap-2">
                                                                {assignments.length > 0 ? assignments.map((a: any, i: number) => (
                                                                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-xl text-[10px] font-bold">
                                                                        <UserCheck size={12} />
                                                                        {/* Deux sources possibles, deux formes : la LISTE des privatisations
                                                                            renvoie `employee_name` deja assemble, la fiche /evenementiel/:id
                                                                            renvoie prenom et nom separes. Le panneau de detail lit l'objet de
                                                                            la liste — d'ou le nom vide quand on ne gere qu'une seule forme. */}
                                                                        {a.employee_name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || 'Salarié supprimé'}
                                                                    </div>
                                                                )) : (
                                                                    <span className="text-[var(--text-muted)] text-[10px] font-bold italic">Aucun interne</span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="space-y-2">
                                                            <p className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Extras à prévoir</p>
                                                            <div className={`inline-flex items-center justify-center px-4 py-1.5 rounded-xl font-bold text-sm ${extras > 0 ? 'bg-orange-500/10 text-orange-400' : 'bg-[var(--interactive-hover)] text-[var(--text-muted)]'}`}>
                                                                {extras} {extras > 1 ? 'Extras' : 'Extra'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                                )}

                                {/* 6. NOTES & DOCUMENTS */}
                                {(selectedEvent.note_text || (selectedEvent.documents && selectedEvent.documents.length > 0)) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4 border-t border-[var(--border-color)]">
                                        {selectedEvent.note_text && (
                                            <div className="space-y-4">
                                                <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-2">
                                                    <AlertCircle size={14} className="text-red-400" /> Note / Message
                                                </h3>
                                                <div className="p-4 bg-red-500/5 rounded-2xl border border-red-500/10 text-sm text-[var(--text-secondary)] whitespace-pre-wrap leading-relaxed">
                                                    {selectedEvent.note_text}
                                                </div>
                                            </div>
                                        )}

                                        {selectedEvent.documents && selectedEvent.documents.length > 0 && (
                                            <div className="space-y-4">
                                                <h3 className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest flex items-center gap-2">
                                                    <Paperclip size={14} className="text-blue-400" /> Documents
                                                </h3>
                                                <div className="space-y-2">
                                                    {selectedEvent.documents.map((doc: any, i: number) => (
                                                        <button
                                                            key={i}
                                                            onClick={() => {
                                                                if (!doc.storage_key) return;
                                                                // Les documents générés côté app (ex: récap PDF des extras confirmés) sont
                                                                // stockés en base64 brut, pas une URL directement ouvrable — les navigateurs
                                                                // bloquent la navigation d'onglet vers une data: URL ("Not allowed to
                                                                // navigate top frame to data URL"), il faut passer par un blob: URL.
                                                                if (doc.storage_key.startsWith('base64:')) {
                                                                    const base64 = doc.storage_key.slice('base64:'.length);
                                                                    const binary = atob(base64);
                                                                    const bytes = new Uint8Array(binary.length);
                                                                    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                                                                    const blob = new Blob([bytes], { type: doc.mime_type || 'application/pdf' });
                                                                    window.open(URL.createObjectURL(blob), '_blank');
                                                                } else {
                                                                    window.open(doc.storage_key, '_blank');
                                                                }
                                                            }}
                                                            className="w-full flex items-center justify-between p-3 bg-blue-500/5 hover:bg-blue-500/10 rounded-xl border border-blue-500/10 transition-all group"
                                                        >
                                                            <div className="flex items-center gap-3 overflow-hidden">
                                                                <FileText size={16} className="text-blue-400 shrink-0" />
                                                                <span className="text-xs font-bold text-[var(--text-primary)] truncate">{doc.file_name}</span>
                                                            </div>
                                                            <ExternalLink size={14} className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <LinkedNotesPanel kind="event" id={selectedEvent.id} />
                            </div>

                            <div className="p-4 md:p-8 bg-[var(--interactive-hover)] border-t border-[var(--border-color)] flex flex-wrap justify-between gap-4 shrink-0">
                                {!(selectedEvent as any).__isReadOnly ? (
                                    <button
                                        onClick={() => {
                                            setConfirmAction({ type: 'deleteEvent', id: selectedEvent.id, label: selectedEvent.type === 'PRIVÉ' ? `${selectedEvent.first_name} ${selectedEvent.last_name}` : selectedEvent.company_name });
                                        }}
                                        className="px-5 py-3 rounded-xl font-bold border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all flex items-center gap-2"
                                    >
                                        <Trash2 size={15} />
                                        Supprimer
                                    </button>
                                ) : (
                                    <div />
                                )}
                                <div className="flex flex-wrap justify-end gap-4">
                                    {!(selectedEvent as any).__isReadOnly && selectedEvent.email && (
                                        <button
                                            onClick={() => setShowSendMessage(true)}
                                            className="px-5 py-3 rounded-xl font-bold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all flex items-center gap-2"
                                        >
                                            <Mail size={15} />
                                            Message personnalisé
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setSelectedEvent(null)}
                                        className="px-6 py-3 rounded-xl font-bold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all"
                                    >
                                        Fermer
                                    </button>
                                    {/* "Booker" — bouton distinct de "Modifier", visible seulement quand des besoins en
                                        extras ont été exprimés (extras_summary avec needed_count > 0) : appelle vers
                                        l'action de booker le personnel demandé. Ouvre exactement le même formulaire
                                        que "Modifier" (aucune page/modale séparée, demande explicite d'Emeni,
                                        2026-08-19) — la distinction est dans QUAND il apparaît, pas dans CE qu'il ouvre. */}
                                    {!(selectedEvent as any).__isReadOnly && Array.isArray((selectedEvent as any).extras_summary) && (selectedEvent as any).extras_summary.some((s: any) => Number(s.needed_count) > 0) && (() => {
                                        // Même règle que sur la bulle (demande d'Emeni) : une fois une demande de booking
                                        // envoyée, "Booker" ne reste visible que pour le destinataire de cette demande, pas
                                        // pour celui qui l'a envoyée (qui a délégué l'action).
                                        const br = (selectedEvent as any).booking_request;
                                        const pendingRequest = !!br && !br.completed_notified_at;
                                        const isRecipient = !!br && user && br.recipient_type === user.type && br.recipient_id === user.id;
                                        if (pendingRequest && !isRecipient) return null;
                                        return (
                                            <button
                                                onClick={() => {
                                                    setBookExtrasTarget(selectedEvent);
                                                    setSelectedEvent(null);
                                                }}
                                                className="px-6 py-3 bg-brand-600 hover:bg-brand-700 text-brand-fg rounded-xl font-bold transition-all flex items-center gap-2"
                                            >
                                                <UserCheck size={16} />
                                                Booker
                                            </button>
                                        );
                                    })()}
                                    {!(selectedEvent as any).__isReadOnly && (
                                        <button
                                            onClick={() => {
                                                setEditingEvent(selectedEvent);
                                                setSelectedEvent(null);
                                                setIsBookerMode(false);
                                                setShowEventForm(true);
                                            }}
                                            className="px-6 py-3 bg-[var(--text-primary)] text-[var(--bg-card)] rounded-xl font-bold hover:opacity-90 transition-all"
                                        >
                                            Modifier
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}

                {selectedEvent && showSendMessage && (
                    <SendCustomMessageModal
                        recipientLabel={selectedEvent.type === 'PRIVÉ' ? `${selectedEvent.first_name} ${selectedEvent.last_name}` : (selectedEvent.company_name || 'l\'organisateur')}
                        tokens={[
                            { token: '{{prenom}}', desc: 'Prénom de l\'organisateur' },
                            { token: '{{nom}}', desc: 'Nom (ou raison sociale) de l\'organisateur' },
                            { token: '{{date}}', desc: 'Date de l\'événement' },
                            { token: '{{entreprise}}', desc: 'Votre établissement' },
                            { token: '{{logo}}', desc: 'Votre logo' },
                        ]}
                        onClose={() => setShowSendMessage(false)}
                        onSend={(subject, body) => moduleApi.sendEventMessage(selectedEvent.id, { subject, body })}
                    />
                )}

                {showBookingEmailSettings && <BookingEmailSettingsModal onClose={() => setShowBookingEmailSettings(false)} />}

                {bookExtrasTarget && (
                    <BookExtrasModal
                        event={bookExtrasTarget}
                        onClose={() => setBookExtrasTarget(null)}
                        onSaved={() => {
                            setBookExtrasTarget(null);
                            if (selectedCalendar) loadEvents(selectedCalendar.id);
                        }}
                    />
                )}

                {requestBookingTarget && (
                    <RequestBookingModal
                        title={requestBookingTarget.type === 'event' ? `Demander à booker — ${requestBookingTarget.label}` : `Demander à booker — ${requestBookingTarget.label}`}
                        onClose={() => {
                            setRequestBookingTarget(null);
                            // Sans ce rechargement, la bulle garde l'ancien booking_request (null) et continue
                            // d'afficher "Booker" côté demandeur jusqu'au prochain rafraîchissement manuel —
                            // signalé par Emeni.
                            if (selectedCalendar) loadEvents(selectedCalendar.id);
                        }}
                        onSend={(recipient) => requestBookingTarget.type === 'event'
                            ? moduleApi.requestEvenementielBooking(requestBookingTarget.id, recipient)
                            : moduleApi.requestEvenementielCalendarBooking(requestBookingTarget.id, recipient)}
                    />
                )}

                {confirmAction && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-sm bg-[var(--bg-card)] rounded-2xl p-8 border border-[var(--border-color)] shadow-2xl"
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center">
                                    <Trash2 size={22} className="text-red-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-[var(--text-primary)]">
                                        {confirmAction.type === 'deleteCalendar' ? 'Supprimer le calendrier' : 'Supprimer la privatisation'}
                                    </h3>
                                    <p className="text-xs text-[var(--text-muted)] mt-0.5">Cette action est irréversible</p>
                                </div>
                            </div>
                            <p className="text-sm text-[var(--text-muted)] mb-8">
                                {confirmAction.type === 'deleteCalendar'
                                    ? <>Voulez-vous vraiment supprimer le calendrier <strong className="text-[var(--text-primary)]">{confirmAction.label}</strong> et toutes ses privatisations ?</>
                                    : <>Voulez-vous vraiment supprimer la privatisation de <strong className="text-[var(--text-primary)]">{confirmAction.label}</strong> ?</>
                                }
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setConfirmAction(null)}
                                    className="flex-1 px-4 py-3 rounded-xl font-bold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all text-sm"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirmAction.type === 'deleteCalendar') handleDeleteCalendar(confirmAction.id);
                                        else if (confirmAction.type === 'deleteEvent') handleDeleteEvent(confirmAction.id);
                                    }}
                                    className="flex-1 px-4 py-3 bg-red-500 text-content rounded-xl font-bold hover:bg-red-600 transition-all text-sm"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {showNewCalendarModal && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-md bg-[var(--bg-card)] rounded-2xl p-10 border border-[var(--border-color)] shadow-2xl"
                        >
                            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-8">Nouveau Calendrier</h2>
                            <form onSubmit={handleCreateCalendar} className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Mois</label>
                                    <select 
                                        value={newCalendar.month}
                                        onChange={e => setNewCalendar({...newCalendar, month: parseInt(e.target.value)})}
                                        className="w-full bg-[var(--bg-app)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] outline-none focus:border-[var(--text-primary)] transition-all"
                                    >
                                        {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Année</label>
                                    <input 
                                        type="number"
                                        value={newCalendar.year}
                                        onChange={e => setNewCalendar({...newCalendar, year: parseInt(e.target.value)})}
                                        className="w-full bg-[var(--bg-app)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] outline-none focus:border-[var(--text-primary)] transition-all"
                                    />
                                </div>
                                <div className="flex gap-4 pt-4">
                                    <button 
                                        type="button"
                                        onClick={() => setShowNewCalendarModal(false)}
                                        className="flex-1 px-6 py-4 rounded-xl font-bold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--interactive-hover)] transition-all"
                                    >
                                        Annuler
                                    </button>
                                    <button 
                                        type="submit"
                                        className="flex-1 bg-[var(--text-primary)] text-[var(--bg-card)] px-6 py-4 rounded-xl font-bold hover:opacity-90 transition-all"
                                    >
                                        Créer
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}

            </AnimatePresence>

            {toastMessage && (
                <div className="fixed top-6 right-6 z-[80] px-4 py-3 rounded-xl bg-[var(--bg-card)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] shadow-2xl">
                    {toastMessage}
                </div>
            )}

            <ExtrasInternalTypesSettings open={showExtrasPositionsModal} onClose={() => setShowExtrasPositionsModal(false)} />
        </div>
    );
};
